import React from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'gatsby'
import LinkElement from 'components/elements/link'
import { calculateResponsiveSize, mediaMax } from 'styles/media-queries'
import { colors } from 'styles/global-styles'

// Translucent "Echelon Noir" backdrop fill (from design). Applied on a dedicated
// layer so only the background is semi-transparent, not the menu text.
const noirBackdrop =
  'var(--Echelon-Noir, linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), #091C2B)'

// Mobile chevron: same glyph as the desktop caret. The box counts the 1.5
// stroke, so background-size:contain fits the design's 19-wide leaf inside 21.
const chevronDown =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20.5' height='9.64287' viewBox='0 0 20.5 9.64287' fill='none'%3E%3Cpath d='M0.750014 0.750014L10.25 8.89287L19.75 0.750014' stroke='%23F8F7F0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")"

export const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: ${calculateResponsiveSize(9)} ${calculateResponsiveSize(18)};
  background: ${colors.noir};
  z-index: 100;
  /* backwards (not both): the entrance keyframe must not keep overriding the
     transform after it ends, so $hidden can drive the slide afterwards. */
  animation: 1.25s ease 0.5s backwards 1 showHeader;
  transform: translateY(${({ $hidden }) => ($hidden ? '-100%' : '0')});
  transition: transform 0.5s ease;
  @keyframes showHeader {
    0% {
      transform: translateY(-100%);
    }
    100% {
      transform: translateY(0);
    }
  }

  ${mediaMax('mobile')} {
    display: flex;
    align-items: center;
    height: ${calculateResponsiveSize(57)};
    padding: 0;
  }
`

export const Logo = styled((props) => <Link {...props} />)`
  display: block;
  width: ${calculateResponsiveSize(74)};
  margin: 0 auto;
  img {
    width: 100%;
  }

  ${mediaMax('mobile')} {
    width: ${calculateResponsiveSize(136)};
  }
`

export const MenuToggle = styled.button`
  position: absolute;
  top: 50%;
  left: ${calculateResponsiveSize(18)};
  width: ${calculateResponsiveSize(15)};
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  transform: translateY(-50%);
  img {
    display: block;
    width: 100%;
  }

  ${mediaMax('mobile')} {
    left: ${calculateResponsiveSize(20)};
    width: ${calculateResponsiveSize(22)};
    /* The close glyph is narrower than the brandmark; keep it on the same axis. */
    &[aria-expanded='true'] img {
      width: ${calculateResponsiveSize(15)};
      margin: 0 auto;
    }
  }
`

// Wrapper clips the panel so it appears to slide out from under the header.
// It is intentionally tall (full viewport) so the closed panel — translated up
// past the clip's top edge — stays hidden, while open sub-menus can extend below
// the panel without being cut off. pointer-events:none lets the empty area below
// pass clicks through to the page.
export const MenuClip = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  height: 100vh;
  overflow: hidden;
  pointer-events: none;

  ${mediaMax('mobile')} {
    /* Mobile panel is full-screen, so the clip stops at the viewport bottom. */
    height: calc(100dvh - ${calculateResponsiveSize(57)});
  }
`

export const MenuPanel = styled.div`
  position: relative;
  /* Tuck an extra 2px past 100% so the bottom border doesn't peek out at the
     header seam due to subpixel rounding when the menu is closed. */
  transform: translateY(calc(-100% - 2px));
  transition: transform 0.5s ease;
  pointer-events: none;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    opacity: 0.75;
    background: ${noirBackdrop};
  }
  &.open {
    transform: translateY(0);
    pointer-events: auto;
  }

  ${mediaMax('mobile')} {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-top: 1px solid ${colors.sand};
    background: ${colors.noir};
    overflow-y: auto;
    overscroll-behavior: contain;
    /* Opaque on mobile — no translucent backdrop layer. */
    &::before {
      content: none;
    }
  }
`

export const MenuList = styled.ul`
  display: flex;
  align-items: center;
  gap: ${calculateResponsiveSize(20)};
  margin: 0;
  padding: 0 ${calculateResponsiveSize(18)};
  list-style: none;

  ${mediaMax('mobile')} {
    flex-direction: column;
    align-items: stretch;
    flex-shrink: 0;
    gap: 0;
    padding: 0;
  }
`

export const MenuListItem = styled.li`
  position: relative;
  display: flex;
  height: ${calculateResponsiveSize(37)};
  padding: ${calculateResponsiveSize(15)} ${calculateResponsiveSize(10)};
  justify-content: center;
  align-items: center;
  gap: ${calculateResponsiveSize(5)};
  margin: 0;
  border-bottom: 2px solid transparent;

  /* Underline on hover/focus (desktop) or when the sub-menu is toggled open
     (touch / click-controlled). */
  &:hover,
  &:focus-within {
    border-bottom-color: var(--Echelon-Sand, ${colors.sand});
  }

  ${({ $subOpen }) =>
    $subOpen &&
    css`
      border-bottom-color: var(--Echelon-Sand, ${colors.sand});
    `}

  ${mediaMax('mobile')} {
    /* Full-width rows. 30 of design padding less the 26/1.5 line box's leading. */
    flex-direction: column;
    align-items: stretch;
    height: auto;
    gap: 0;
    padding: ${calculateResponsiveSize(20)} ${calculateResponsiveSize(40)};
    border-bottom: 1px solid ${colors.sand};
  }
`

// Title + caret line of a menu item. Transparent on desktop so both stay direct
// flex children of the item; on mobile it becomes the tappable row.
export const MenuRow = styled.div`
  display: contents;

  ${mediaMax('mobile')} {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`

export const MenuLink = styled(LinkElement)`
  color: ${colors.white};
  text-decoration: none;
  font-family: 'Gotham', sans-serif;
  font-size: ${calculateResponsiveSize(10)};
  font-weight: 500;
  line-height: 140%;
  white-space: nowrap;
  &:hover {
    text-decoration: none;
  }

  ${mediaMax('mobile')} {
    /* Mobile Menu Titles */
    color: ${colors.sand};
    font-size: ${calculateResponsiveSize(26)};
    line-height: 1.5;
  }
`

// Chevron button that toggles the sub-menu. A real <button> (not a pseudo
// element) so it is tappable on touch and operable from the keyboard. Padding
// enlarges the tap target; matching negative margin keeps the 5px gap and the
// caret's visual position unchanged.
export const SubMenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${calculateResponsiveSize(10)} ${calculateResponsiveSize(8)};
  margin: ${calculateResponsiveSize(-10)} ${calculateResponsiveSize(-8)};
  border: 0;
  background: none;
  cursor: pointer;
  font-size: ${calculateResponsiveSize(10)};

  &::after {
    content: '';
    width: ${calculateResponsiveSize(7)};
    height: ${calculateResponsiveSize(3)};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='3' viewBox='0 0 7 3' fill='none'%3E%3Cpath d='M6.5 2.625L3.5 0.375L0.5 2.625' stroke='%23F8F7F0' stroke-width='0.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transform: translateY(0.06em);
    transition: transform 0.25s ease;
  }

  /* Flip the chevron down on hover/focus (desktop) or when toggled open. */
  ${MenuListItem}:hover &::after,
  ${MenuListItem}:focus-within &::after,
  &[aria-expanded='true']::after {
    /* Base nudge + optical compensation so the flipped chevron stays centered. */
    transform: translateY(0.14em) rotate(180deg);
  }

  ${mediaMax('mobile')} {
    /* The whole row toggles the accordion; the caret sits on its right edge. */
    position: absolute;
    inset: 0;
    justify-content: flex-end;
    padding: 0;
    margin: 0;

    &::after {
      width: ${calculateResponsiveSize(21)};
      height: ${calculateResponsiveSize(10)};
      background-image: ${chevronDown};
    }

    /* No hover on touch: the caret follows the toggled state alone. */
    &::after,
    ${MenuListItem}:hover &::after,
    ${MenuListItem}:focus-within &::after {
      transform: none;
    }
    &[aria-expanded='true']::after,
    ${MenuListItem}:hover &[aria-expanded='true']::after,
    ${MenuListItem}:focus-within &[aria-expanded='true']::after {
      transform: rotate(180deg);
    }
  }
`

// Collapses the mobile accordion; transparent on desktop, where the sub-menu is
// an absolutely positioned dropdown instead.
export const SubMenuWrap = styled.div`
  display: contents;

  ${mediaMax('mobile')} {
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
    transition: grid-template-rows 0.4s ease;
  }
`

// Vertical dropdown revealed on hover/focus of a menu item that has children.
export const SubMenu = styled.ul`
  position: absolute;
  /* +2px so the dropdown clears the item's bottom border instead of covering it. */
  top: calc(100% + 2px);
  left: 0;
  display: flex;
  width: ${calculateResponsiveSize(297)};
  flex-direction: column;
  align-items: flex-start;
  gap: ${calculateResponsiveSize(14)};
  margin: 0;
  padding: ${calculateResponsiveSize(14)} ${calculateResponsiveSize(19)};
  list-style: none;
  background: rgba(9, 28, 43, 0.75);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.25s ease, visibility 0.25s ease;

  ${MenuListItem}:hover &,
  ${MenuListItem}:focus-within & {
    opacity: 1;
    visibility: visible;
  }

  ${({ $open }) =>
    $open &&
    css`
      opacity: 1;
      visibility: visible;
    `}

  /* First item's dropdown sits flush with the page edge (offset out by the
     list's left padding). */
  ${MenuListItem}:first-child & {
    left: -${calculateResponsiveSize(18)};
  }

  ${mediaMax('mobile')} {
    /* In-flow accordion body; SubMenuWrap does the collapsing. Padding would
       keep the collapsed grid row off zero, so the ends carry margins instead. */
    position: static;
    width: 100%;
    min-height: 0;
    gap: ${calculateResponsiveSize(18)};
    padding: 0;
    background: none;
    opacity: 1;
    visibility: visible;
    overflow: hidden;
    > li:first-child {
      margin-top: ${calculateResponsiveSize(14)};
    }
    > li:last-child {
      margin-bottom: ${calculateResponsiveSize(14)};
    }
  }
`

export const SubMenuItem = styled.li`
  margin: 0;
`

export const SubMenuLink = styled(LinkElement)`
  display: inline-block;
  color: ${colors.sand};
  text-decoration: none;
  font-family: 'Gotham', sans-serif;
  font-size: ${calculateResponsiveSize(10)};
  font-weight: 500;
  line-height: 140%;
  text-transform: uppercase;
  white-space: nowrap;
  transform-origin: left center;
  transition: transform 0.2s ease;
  &:hover {
    text-decoration: none;
    transform: scale(1.05);
  }

  ${mediaMax('mobile')} {
    font-size: ${calculateResponsiveSize(15)};
    font-weight: 400;
    line-height: 1.5;
    &:hover {
      transform: none;
    }
  }
`

// The menu panel carries the footer blocks on mobile, pinned to its bottom.
export const MenuFooter = styled.div`
  display: none;

  ${mediaMax('mobile')} {
    display: flex;
    flex-direction: column;
    margin-top: auto;
    padding: ${calculateResponsiveSize(40)} ${calculateResponsiveSize(40)} ${calculateResponsiveSize(34)};
    color: ${colors.sand};
    a {
      color: ${colors.sand};
      text-decoration: none !important;
    }
  }
`
